import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CurrenciesService } from 'src/app/api/currencies.service';
import { Currency } from 'src/app/models/currency';
import { MetadataService } from 'src/app/api/metadata.service';
import { CryptoPriceData, PriceChartDuration } from 'src/app/models/price-chart';
import { environment } from 'src/environments/environment';
import { FormControl } from '@angular/forms';
import { AuthService } from 'src/app/api/auth.service';

export type SortColumn = 'name' | 'price' | 'change' | null;
export type SortDirection = 'asc' | 'desc';
export type ViewMode = 'table' | 'card';

@Component({
    selector: 'crypto-prices',
    templateUrl: './crypto-prices.component.html',
    styleUrls: ['./crypto-prices.component.scss'],
    standalone: false
})
export class CryptoPricesComponent implements OnInit, OnChanges {
  PriceChartDuration = PriceChartDuration;

  @Input() count: number = null;
  @Input() showHeader = true;
  @Input() showUpdateDate = true;
  @Input() search: string;
  @Output() update = new EventEmitter<Date>();

  loading = true;
  error = false;

  fiatCurrency: string;
  fiatCurrencies: Currency[];
  currencies: Currency[] = [];
  filteredCurrencies: string[];
  duration = (localStorage.getItem('priceDuration') as PriceChartDuration) || PriceChartDuration.OneDay;
  lastUpdateDate: Date;

  // Sorting state
  sortColumn: SortColumn = null;
  sortDirection: SortDirection = 'asc';
  priceDataMap = new Map<string, CryptoPriceData>();

  searchControl = new FormControl(null);
  showCharts = window.innerWidth >= 992 || localStorage.getItem('showPriceCharts') === 'true';
  viewMode: ViewMode = (localStorage.getItem('priceViewMode') as ViewMode) || 'table';
  favorites = new Set<string>();

  // Duration labels for mobile dropdown
  durationOptions = [
    { value: PriceChartDuration.OneDay, label: '24H' },
    { value: PriceChartDuration.SevenDays, label: '7D' },
    { value: PriceChartDuration.OneMonth, label: '1M' },
    { value: PriceChartDuration.ThreeMonths, label: '3M' },
    { value: PriceChartDuration.OneYear, label: '1Y' },
  ];

  constructor(
    private  metadata: MetadataService,
    private currenciesService: CurrenciesService,
    private auth: AuthService
  ) { }

  ngOnInit() {
    this.loadFavorites();
    this.getDefaultFiatCurrency();
    this.getFiatCurrencies();


    this.searchControl.valueChanges.subscribe(val => {
      this.handleSearch(val);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const searchChange = changes['search'];

    if (searchChange && searchChange.currentValue != searchChange.previousValue) {
      this.searchControl.setValue(searchChange.currentValue);
    }
  }

  getDefaultFiatCurrency() {
    const saved = localStorage.getItem('priceFiatCurrency');
    if (saved) {
      this.setFiatCurrency(saved);
      return;
    }
    this.metadata.userCurrency().subscribe(response => {
      this.setFiatCurrency(response);
    }, err => {
      this.setFiatCurrency(environment.currencies.defaultCurrency);
    });
  }

  getCryptoCurrencies() {
    this.loading = true;

    this.currenciesService.getCryptoWithSupportedBase(this.fiatCurrency).subscribe(response => {
      this.loading = false;
      this.currencies = response;

      if (this.searchControl.value) {
        this.handleSearch(this.searchControl.value);
      } else {
        this.filteredCurrencies = this._getCurrencyCodes(this.currencies, this.count);
      }
    }, err => {
      this.loading = false;
    });
  }

  getFiatCurrencies() {
    if(this.auth.getToken()){
      this.currenciesService.getFiatWithoutUsd().subscribe(currencies => {
        this.fiatCurrencies = currencies;
      })
    } else {
      this.currenciesService.getCountryAllowedFiat().subscribe(response => {
        this.currenciesService.getFiatFiltered(item => response.includes(item.code)).subscribe(currencies =>{
          this.fiatCurrencies = currencies;
        });
      });
    }
  }

  setFiatCurrency(currency: string) {
    this.fiatCurrency = currency;
    localStorage.setItem('priceFiatCurrency', currency);
    this.priceDataMap.clear();
    this.getCryptoCurrencies();
  }

  changeDuration(duration: PriceChartDuration) {
    this.duration = duration;
    localStorage.setItem('priceDuration', duration);
  }

  toggleCharts() {
    this.showCharts = !this.showCharts;
    localStorage.setItem('showPriceCharts', String(this.showCharts));
  }

  setViewMode(mode: ViewMode) {
    this.viewMode = mode;
    localStorage.setItem('priceViewMode', mode);
  }

  onDataChange(data: CryptoPriceData) {
    if (data?.mainCurrency) {
      this.priceDataMap.set(data.mainCurrency, data);
    }
    this.lastUpdateDate = new Date();
    this.update.next(this.lastUpdateDate);
  }

  clearSearchValue() {
    this.searchControl.setValue(null);
  }

  handleSearch(q: string) {
    if (!q) {
      this.filteredCurrencies = this._getCurrencyCodes(this.currencies, this.count);
    } else {
      const results: string[] = [];
      this.currencies?.forEach(item => {
        if (item.code.toLowerCase().includes(q.toLowerCase()) || item.name.toLowerCase().includes(q.toLowerCase())) {
          results.push(item.code);
        }
      });

      this.filteredCurrencies = results;
    }
  }

  toggleSort(column: SortColumn) {
    if (this.sortColumn === column) {
      // Same column: cycle through directions then back to null
      // name: asc → desc → null
      // price/change: desc → asc → null
      const defaultDir = column === 'name' ? 'asc' : 'desc';
      const nextDir = defaultDir === 'asc' ? 'desc' : 'asc';

      if (this.sortDirection === defaultDir) {
        this.sortDirection = nextDir;
      } else {
        this.sortColumn = null;
      }
    } else {
      // New column: set default direction
      this.sortColumn = column;
      this.sortDirection = column === 'name' ? 'asc' : 'desc';
    }
  }

  toggleFavorite(code: string) {
    if (this.favorites.has(code)) {
      this.favorites.delete(code);
    } else {
      this.favorites.add(code);
    }
    this.saveFavorites();
  }

  isFavorite(code: string): boolean {
    return this.favorites.has(code);
  }

  private loadFavorites() {
    try {
      const stored = localStorage.getItem('cryptoPriceFavorites');
      if (stored) {
        const arr = JSON.parse(stored);
        this.favorites = new Set(arr);
      }
    } catch {}
  }

  private saveFavorites() {
    localStorage.setItem('cryptoPriceFavorites', JSON.stringify([...this.favorites]));
  }

  get sortedCurrencies(): Currency[] {
    const sorted = !this.sortColumn ? [...this.currencies] : [...this.currencies].sort((a, b) => {
      let comparison = 0;

      switch (this.sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'price': {
          const priceA = Number(this.priceDataMap.get(a.code)?.baseCurrency?.lastPrice);
          const priceB = Number(this.priceDataMap.get(b.code)?.baseCurrency?.lastPrice);
          if (isNaN(priceA) && isNaN(priceB)) return 0;
          if (isNaN(priceA)) return 1;
          if (isNaN(priceB)) return -1;
          comparison = priceA - priceB;
          break;
        }
        case 'change': {
          const changeA = Number(this.priceDataMap.get(a.code)?.priceChangePercentage);
          const changeB = Number(this.priceDataMap.get(b.code)?.priceChangePercentage);
          if (isNaN(changeA) && isNaN(changeB)) return 0;
          if (isNaN(changeA)) return 1;
          if (isNaN(changeB)) return -1;
          comparison = changeA - changeB;
          break;
        }
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Pin favorites to the top, preserving sort order within each group
    if (this.favorites.size === 0) {
      return sorted;
    }
    const favs = sorted.filter(c => this.favorites.has(c.code));
    const rest = sorted.filter(c => !this.favorites.has(c.code));
    return [...favs, ...rest];
  }

  trackByCurrencyCode(_: number, currency: Currency) {
    return currency.code;
  }

  private _getCurrencyCodes(currencies: Currency[], count: number) {
    const codes = currencies.map(item => item.code);

    if (count) {
      return codes.splice(0, count);
    } else {
      return codes;
    }
  }
}
